import { Color, Texture } from "three";
import { IPropertyValueAccessor, Metadata, PropertyAccessContext } from "../../properties/common/accessor.js";
import { getMatrixDimension, isColorLike, isMatrixLike, isRGBColorLike, isVectorLike } from "../../properties/common/types.js";
import { shallowCloneObject } from "../../../base/common/helpers.js";
import { PropertyMetadata, ValueMetadata } from "../../properties/common/properties.js";
import { isAnimationActionLike, isAnimationMixer, isObject3D, isTexture } from "./helper.js";
import { ValueConversionService } from "../../properties/common/converter.js";
import { isInputNode, InputNodeLike } from "./helper.nodes.js";
import { URI } from "../../../base/common/uri.js";

type ObjectWithCopyfunction = object & { copy: Function };
function isObjectWithCopyFunction(object: any): object is ObjectWithCopyfunction {
    return object && typeof object === "object" && typeof object.copy === "function";
}

function handleThreeClone(value: object): any {
    // Native math clones detach matrix storage and quaternion/Euler change callbacks.
    if ("clone" in value && typeof value.clone === "function") return value.clone();
    const clone = shallowCloneObject(value);
    if (isMatrixLike(value)) Object.assign(clone, { elements: Array.from(value.elements) });
    if (isObjectWithCopyFunction(clone)) {
        return clone.copy(value);
    }
    return clone;
}


// #region Metadata

export const ThreeObjectMetadataAccessor = new class implements IPropertyValueAccessor {

    private readonly metadataKey = "needle-inspector-metadata";

    canReadMetadata(object: object): boolean {
        if (object && typeof object === "object" && 'userData' in object) {
            return true;
        }
        return false;
    }
    async readMetadata(object: object): Promise<ValueMetadata | undefined> {
        if (object && typeof object === "object" && 'userData' in object) {
            const userData = (object as any)['userData'];
            if (userData && this.metadataKey in userData && typeof userData[this.metadataKey] === "object") {
                return userData[this.metadataKey] as ValueMetadata;
            }
        }
        return undefined;
    }

    canWriteMetadata(object: object, metadata: ValueMetadata): boolean {
        if (object && typeof object === "object") {
            return true;
        }
        return false;
    }
    async writeMetadata(object: Record<string, any>, metadata: ValueMetadata): Promise<void> {
        if (object && typeof object === "object") {
            object['userData'] = object['userData'] || {};
            object['userData'][this.metadataKey] = metadata;
            return;
        }
    }
}


// #region Color

type ColorWithMaybeAlpha = Color & { a?: number, alpha?: number };

const acceptedHexLengths = [4, 5, 7, 9]; // #RGB, #RGBA, #RRGGBB, #RRGGBBAA

/** "9414911" — a colour a caller stringified. 0..0xffffff, digits only. */
function isDecimalColorString(value: string): boolean {
    if (!/^[0-9]+$/.test(value)) return false;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 0xffffff;
}

export const ColorPropertyAccessor = new class implements IPropertyValueAccessor {

    canWrite(object: Record<string, any>, key: string, newValue: unknown, meta: Metadata): boolean {

        // Reject InputNode.value - let TSLInputNodeAccessor handle it
        if (key === "value" && isInputNode(object)) {
            return false;
        }

        const currentValue = object[key];
        const currentValueIsColor = isColorLike(currentValue);

        if (currentValueIsColor || PropertyMetadata.testAccept({ accept: meta.accept }, "color/*")) {

            if (newValue instanceof Color) return true;
            if (typeof newValue === "number") return true;
            if (isRGBColorLike(newValue)) return true;

            if (typeof newValue === "string") {
                // Ignore data or URL strings
                if (newValue.startsWith("file:") ||
                    newValue.startsWith("data:") ||
                    newValue.startsWith("http:") ||
                    newValue.startsWith("https:")) {
                    return false;
                }
                // is string a HEX string?
                if (newValue.startsWith("#")) {
                    if (acceptedHexLengths.includes(newValue.length)) {
                        return true;
                    }
                }
                else if (newValue.startsWith("0x")) {
                    // is string a 0xRRGGBB or 0xRRGGBBAA string? The accepted
                    // lengths COUNT the "#", so the bare hex part is one shorter —
                    // comparing it directly rejected every 0xRRGGBB ever passed
                    const hexPart = newValue.substring(2);
                    if (acceptedHexLengths.includes(hexPart.length + 1)) {
                        return true;
                    }
                }
                // a DECIMAL string — what a caller sends when it stringifies a
                // number (an AI tool argument routinely does). Taking it here is
                // what keeps the fallback accessor from assigning the raw string
                // over the Color.
                else if (isDecimalColorString(newValue)) {
                    return true;
                }
            }

            return false;
        }

        return false;
    }
    async writeValue(object: Record<string, any>, key: string, newValue: any) {

        let current = object[key] as ColorWithMaybeAlpha;
        if (current === null || !isColorLike(current)) {
            current = new Color();
            object[key] = current;
        }

        // console.log("ColorPropertyAccessor.writeValue", { object, key, newValue, current });

        if (isColorLike(newValue) && ("copy" in current && typeof current.copy === "function")) {
            current.copy(newValue as Color);
        }
        else if (typeof newValue === "number") {
            current.set(newValue);
        }
        else if (typeof newValue === "string") {
            // a decimal string IS a number — Color.set() would read it as a CSS
            // style string, fail to parse, and silently leave the colour white
            current.set(isDecimalColorString(newValue) ? Number(newValue) : newValue);
        }
        else if (isRGBColorLike(newValue)) {
            const col = newValue as Partial<ColorWithMaybeAlpha>;
            current.r = col.r ?? current.r;
            current.g = col.g ?? current.g;
            current.b = col.b ?? current.b;
            current.a = col.a ?? current.a;
            current.alpha = col.a ?? col.alpha ?? current.alpha;
        }
        else {
            console.error("Cannot set Color property with value:", newValue);
            throw new Error("Invalid value for Color property");
        }
    }
    cloneValue(object: object, key: string, value: any) {
        return handleThreeClone(value);
    }
}



// #region Vector

type EulerLike = { _x: number; _y: number; _z: number; _order: string; };
function isEulerLike(obj: any): obj is EulerLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    return '_x' in obj && '_y' in obj && '_z' in obj && '_order' in obj;
}

export const VectorPropertyAccessor = new class implements IPropertyValueAccessor {

    canWrite(object: Record<string, any>, key: string, newValue: unknown, meta: Metadata): boolean {
        // Reject InputNode.value - let TSLInputNodeAccessor handle it
        if (key === "value" && isInputNode(object)) {
            return false;
        }

        const currentValueIsVector = object[key] && typeof object[key] === "object" &&
            ('x' in object[key]) && ('y' in object[key]);

        if (currentValueIsVector || PropertyMetadata.testAccept({ accept: meta.accept }, "vector/*")) {
            if (isObjectWithCopyFunction(newValue)) return true;
            if (isEulerLike(newValue)) return true;
            if (isVectorLike(newValue)) return true;
            return false;
        }

        return false;
    }

    async writeValue(object: Record<string, any>, key: string, newValue: any) {
        const current = object[key];
        if (newValue instanceof current.constructor) {
            current.copy(newValue);
        }
        else if (isEulerLike(newValue)) {
            const euler = newValue as Partial<EulerLike>;
            current.x = euler._x ?? current._x;
            current.y = euler._y ?? current._y;
            current.z = euler._z ?? current._z;
            current.order = euler._order ?? current._order;
        }
        else if (isVectorLike(newValue)) {
            const vec = newValue as Partial<{ x: number; y: number; z?: number; w?: number }>;
            current.x = vec.x ?? current.x;
            current.y = vec.y ?? current.y;
            if ('z' in current) {
                current.z = vec.z ?? current.z;
            }
            if ('w' in current) {
                current.w = vec.w ?? current.w;
            }
        }
        else {
            console.error("Cannot set Vector property with value:", newValue);
            throw new Error("Invalid value for Vector property");
        }
    }
    cloneValue(object: object, key: string, value: any) {
        return handleThreeClone(value);
    }
}


// #region Object reference (scene nodes)

/**
 * Scene-object REFERENCES are identity values: the undo snapshot must be the
 * SAME node (undo restores the reference, never a copy of the object) — and
 * shallow-cloning an Object3D throws anyway (position/rotation/quaternion/
 * scale are read-only accessor properties on the prototype). Clone-only: the
 * write path stays with the default accessor's plain assignment.
 */
export const ObjectReferenceAccessor = new class implements IPropertyValueAccessor {
    canClone(object: Record<string, any>, key: string, value: unknown, meta: Metadata): boolean {
        return isObject3D(value);
    }
    cloneValue(object: object, key: string, value: unknown): unknown {
        return value;
    }
}

// #region Matrix

type MatrixWriteLike = { elements: ArrayLike<number> };

export const MatrixPropertyAccessor = new class implements IPropertyValueAccessor {

    canWrite(object: Record<string, any>, key: string, newValue: unknown, meta: Metadata): boolean {
        return isMatrixLike(object[key]) && isMatrixLike(newValue);
    }

    async writeValue(object: Record<string, any>, key: string, newValue: MatrixWriteLike) {
        const current = object[key];
        if (!isMatrixLike(current) || !isMatrixLike(newValue)) {
            throw new Error("Invalid value for Matrix property");
        }

        if ("copy" in current && typeof current.copy === "function" && current.constructor === (newValue as any).constructor) {
            current.copy(newValue);
            return;
        }

        const dimension = getMatrixDimension(current);
        const incomingDimension = getMatrixDimension(newValue);
        if (!dimension || dimension !== incomingDimension) {
            throw new Error("Matrix dimension mismatch");
        }

        const elements = Array.from(newValue.elements);
        if ("fromArray" in current && typeof current.fromArray === "function") {
            current.fromArray(elements);
            return;
        }

        if ("elements" in current) {
            for (let i = 0; i < elements.length; i++) {
                current.elements[i] = elements[i];
            }
            return;
        }

        throw new Error("Matrix property does not support writing elements");
    }

    canClone(object: Record<string, any>, key: string, value: unknown, meta: Metadata): boolean {
        return isMatrixLike(value);
    }

    cloneValue(object: object, key: string, value: any) {
        return handleThreeClone(value);
    }
}




// #region Texture

/** Image file extensions that can be loaded as textures */
const textureFileExtensionRegex = /\.(png|jpg|jpeg|gif|bmp|webp|svg|exr|hdr|hdri|ktx2)(\?|$)/i;

/** Check if a string looks like a URL that could be loaded as a texture */
function isTextureUrl(value: string): boolean {
    if (value.startsWith("data:image/")) return true;
    if (textureFileExtensionRegex.test(value)) return true;
    return false;
}

export const TexturePropertyAccessor = new class implements IPropertyValueAccessor {

    canWrite(object: Record<string, any>, key: string, newValue: unknown, meta: Metadata): boolean {

        const currentValueIsTexture = object[key] && typeof object[key] === "object" &&
            ('isTexture' in object[key]) && object[key]['isTexture'] === true;

        if (currentValueIsTexture || PropertyMetadata.testAccept({ accept: meta.accept }, "image/*")) {

            if (typeof newValue === "object") {
                if (isTexture(newValue)) return true;
            }

            // is newValue a url string?
            if (typeof newValue === "string") {
                if (isTextureUrl(newValue)) return true;
            }
            // is newValue a URI object?
            else if (newValue instanceof URI) {
                if (isTextureUrl(newValue.path) || isTextureUrl(newValue.toString())) return true;
            }
            return false;
        }

        return false;
    }

    async writeValue(object: Record<string, any>, key: string, newValue: any, context?: PropertyAccessContext) {

        // If it's already a texture, assign directly
        if (isTexture(newValue)) {
            object[key] = newValue;
            return;
        }

        // Convert URL string or URI to texture
        const url = newValue instanceof URI ? newValue.toString() : newValue;

        if (typeof url === "string" && isTextureUrl(url)) {
            // Use provided usageHint if available, otherwise infer from property
            const usageHint = (context?.usageHint as "skybox" | undefined) ?? this.inferUsageHint(object, key);

            const texture = await ValueConversionService.convert(url, {
                targetType: "texture",
                usageHint
            });

            if (isTexture(texture)) {
                object[key] = texture;
                return;
            }
            throw new Error(`Failed to convert URL to Texture: ${url}`);
        }

        throw new Error("Failed to set Texture property: unsupported value type");
    }

    /**
     * Infer the usage hint for texture conversion based on the property being set.
     * This is used as a fallback when no explicit usageHint is provided.
     */
    private inferUsageHint(object: Record<string, any>, key: string): "skybox" | undefined {
        // Scene environment and background properties should use equirectangular mapping
        if ('isScene' in object && object['isScene'] === true) {
            if (key === 'environment' || key === 'background') {
                return "skybox";
            }
        }
        return undefined;
    }

    cloneValue(object: object, key: string, value: unknown) {
        if (value instanceof Texture) {
            return value.clone();
        }
        throw new Error("Cannot clone non-Texture value as Texture");
    }
}



// #region TSL InputNode

/**
 * Accessor for TSL InputNode values.
 *
 * IMPORTANT: This accessor creates NEW instances of Color/Vector instead of mutating existing ones.
 *
 * Why mutation doesn't work:
 * - TSL wraps objects like Color in reactive proxies that intercept mutations (.set(), .copy(), direct property assignment)
 * - These mutations route through TSL's .assign() system, which requires an Fn() context
 * - Without this context, you get: "No stack defined for assign operation"
 *
 * Why direct assignment works:
 * - inputNode.value is a plain JavaScript property with no custom setter
 * - Direct assignment (inputNode.value = newColor) bypasses TSL's reactive assignment system
 * - This is the recommended pattern for updating InputNode values outside of Fn() contexts
 */
export const TSLInputNodeAccessor = new class implements IPropertyValueAccessor {

    canRead(object: object, key: string, meta: Metadata): boolean {
        // Check if we're accessing the 'value' property of an InputNode
        if (key === "value" && isInputNode(object)) {
            return true;
        }
        return false;
    }

    readValue(object: object, key: string, context: PropertyAccessContext): unknown {
        if (key === "value" && isInputNode(object)) {
            const inputNode = object as InputNodeLike;
            return inputNode.value;
        }
        return undefined;
    }

    canWrite(object: object, key: string, newValue: unknown, meta: Metadata): boolean {
        // Check if we're writing to the 'value' property of an InputNode
        if (key !== "value" || !isInputNode(object)) {
            return false;
        }

        const inputNode = object as InputNodeLike;
        const currentValue = inputNode.value;

        // Handle Color values
        if (isColorLike(currentValue)) {
            // Accept Color instances, hex strings, or RGB-like objects
            if (newValue instanceof Color) return true;
            if (typeof newValue === "number") return true; // Hex number
            if (typeof newValue === "string") {
                // Accept #RGB, #RGBA, #RRGGBB, #RRGGBBAA, 0xRRGGBB formats
                if (newValue.startsWith("#") || newValue.startsWith("0x")) {
                    return true;
                }
            }
            if (isRGBColorLike(newValue)) return true;
        }

        // Handle Vector values (Vector2, Vector3, Vector4)
        if (isVectorLike(currentValue)) {
            if (isVectorLike(newValue)) return true;
            // Accept objects with matching dimensions
            if (typeof newValue === "object" && newValue !== null) {
                if ('x' in newValue && 'y' in newValue) {
                    return true;
                }
            }
        }

        // For primitive types (number, boolean, string), allow direct assignment
        const currentType = typeof currentValue;
        const newType = typeof newValue;
        if (currentType === newType && (currentType === "number" || currentType === "boolean" || currentType === "string")) {
            return true;
        }

        // Allow null/undefined assignments
        if (newValue === null || newValue === undefined) {
            return true;
        }

        return false;
    }

    async writeValue(object: object, key: string, newValue: unknown, context: PropertyAccessContext): Promise<void> {
        if (key !== "value" || !isInputNode(object)) {
            throw new Error("TSLInputNodeAccessor can only write to InputNode.value");
        }

        const inputNode = object as InputNodeLike;
        const currentValue = inputNode.value;

        // Handle Color values - create a new Color and assign it to inputNode.value
        // This avoids TSL stack errors from trying to mutate the existing Color
        if (isColorLike(currentValue)) {
            let newColor: Color;

            if (newValue instanceof Color) {
                newColor = newValue.clone();
            } else if (typeof newValue === "number") {
                newColor = new Color(newValue);
            } else if (typeof newValue === "string") {
                newColor = new Color(newValue);
            } else if (isRGBColorLike(newValue)) {
                const rgb = newValue as { r: number; g: number; b: number };
                newColor = new Color(rgb.r, rgb.g, rgb.b);
            } else {
                throw new Error("Invalid value for Color in InputNode");
            }

            // Assign the new color to the InputNode's value
            inputNode.value = newColor;
            return;
        }

        // Handle Vector values - create a new Vector and assign it to inputNode.value
        if (isVectorLike(currentValue)) {
            const currentVec = currentValue as { x: number; y: number; z?: number; w?: number; constructor: any };
            let newVec: any;

            if (isVectorLike(newValue) && 'constructor' in currentVec) {
                // Clone the new vector using the same constructor as current
                newVec = new currentVec.constructor();
                if ('copy' in newVec && typeof newVec.copy === "function") {
                    newVec.copy(newValue);
                }
            } else if (typeof newValue === "object" && newValue !== null && 'constructor' in currentVec) {
                // Create new vector with updated components
                newVec = new currentVec.constructor();
                const newVecData = newValue as Partial<{ x: number; y: number; z: number; w: number }>;
                newVec.x = newVecData.x ?? currentVec.x;
                newVec.y = newVecData.y ?? currentVec.y;
                if ('z' in currentVec) {
                    newVec.z = newVecData.z ?? currentVec.z;
                }
                if ('w' in currentVec) {
                    newVec.w = newVecData.w ?? currentVec.w;
                }
            } else {
                throw new Error("Invalid value for Vector in InputNode");
            }

            // Assign the new vector to the InputNode's value
            inputNode.value = newVec;
            return;
        }

        // For primitive types or objects without copy semantics, assign directly
        inputNode.value = newValue;
    }

    cloneValue(object: object, key: string, value: unknown): unknown {
        // For Color and Vector types, use their clone() or copy() methods
        if (typeof value === "object" && value !== null) {
            if ('clone' in value && typeof (value as any).clone === "function") {
                return (value as any).clone();
            }
            if ('copy' in value && typeof (value as any).copy === "function") {
                // Create a new instance using the constructor and copy
                const constructor = (value as any).constructor;
                if (constructor) {
                    const clone = new constructor();
                    if ('copy' in clone && typeof clone.copy === "function") {
                        clone.copy(value);
                        return clone;
                    }
                }
            }
        }

        // For primitives, return as-is
        if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") {
            return value;
        }

        // Fallback: return the value (might need deep clone for complex objects)
        return value;
    }
}


// #region Animation timing

/**
 * Routes writes on mixer/action timing properties through the three APIs that
 * keep the animation system consistent — a raw property assignment is not
 * enough:
 * - `mixer.time`: `setTime()` resets and re-advances EVERY action to the
 *   absolute time (respecting per-action timeScales); a raw write changes the
 *   number without retiming anything.
 * - `action.time`: assignable directly, but the pose only re-evaluates on the
 *   next `mixer.update()` — force `update(0)` so scrubbing works while the
 *   app's loop is paused.
 * - `action.repetitions`: a count must go through `setLoop(mode, count)`.
 */
export const AnimationTimeAccessor = new class implements IPropertyValueAccessor {

    canWrite(object: Record<string, any>, key: string, newValue: unknown): boolean {
        const isNumber = typeof newValue === "number" && !Number.isNaN(newValue);
        if (isAnimationMixer(object)) return key === "time" && isNumber;
        if (isAnimationActionLike(object)) {
            if (key === "time" || key === "repetitions") return isNumber;
            // isRunning is a METHOD — the accessor MUST intercept, or the raw
            // write shadows it with a boolean and breaks the action
            if (key === "isRunning") return typeof newValue === "boolean";
        }
        return false;
    }

    async writeValue(object: Record<string, any>, key: string, newValue: number | boolean): Promise<void> {
        if (isAnimationMixer(object)) {
            (object as unknown as { setTime(t: number): void }).setTime(newValue as number);
            return;
        }
        const action = object as unknown as {
            time: number;
            loop: number;
            enabled: boolean;
            paused: boolean;
            play(): void;
            stop(): void;
            setLoop(mode: number, repetitions: number): void;
            _mixer?: { update(delta: number): void };
        };
        if (key === "repetitions") {
            action.setLoop(action.loop, newValue as number);
            return;
        }
        if (key === "isRunning") {
            if (newValue) {
                // play() only schedules — a disabled/paused action would still
                // not run, so clear both
                action.enabled = true;
                action.paused = false;
                action.play();
            }
            else {
                action.stop();
            }
        }
        else {
            action.time = newValue as number;
        }
        // apply the pose immediately even when the app's loop is paused
        try { action._mixer?.update(0); } catch { /* host mixer — stay defensive */ }
    }
};
